import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { useLearn } from "@/contexts/LearnContext";
import { ArrowRight, Shield } from "lucide-react";

export default function WelcomePage() {
  const { registerUser, user, loading } = useLearn();
  const [name, setName] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("learner_name") || "";
    } catch {
      return "";
    }
  });
  const [err, setErr] = useState("");
  const [loadingBtn, setLoadingBtn] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const n = name.trim();
    if (n.length < 2) {
      setErr("Please enter your full name (at least 2 characters).");
      return;
    }
    setLoadingBtn(true);
    try {
      await registerUser(n);
      navigate("/home");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save your name. Please try again.");
    } finally {
      setLoadingBtn(false);
    }
  };

  if (loading || user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4"></div>
        <p className="text-muted-foreground text-sm">
          {user ? "Redirecting..." : "Loading module..."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen welcome-hero relative overflow-hidden">
      {/* Corner logos */}
      <div className="absolute top-6 left-6 md:top-10 md:left-10 z-10" data-testid="welcome-logo-left">
        <LogoMark className="h-14 w-14 md:h-20 md:w-20" />
      </div>
      <div className="absolute top-6 right-6 md:top-10 md:right-10 z-10" data-testid="welcome-logo-right">
        <LogoMark className="h-14 w-14 md:h-20 md:w-20" />
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-32 md:pt-44 pb-20 relative">
        {/* Vertical meta */}
        <div className="hidden md:block absolute left-4 top-52 rotate-[-90deg] origin-top-left text-[10px] tracking-[0.4em] uppercase text-muted-foreground">
          Steadfast IT Consulting · eLearning
        </div>

        <div className="text-xs tracking-[0.3em] uppercase text-primary mb-6">
          40-Hour University Programme
        </div>

        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1] tracking-tight">
          <span className="italic font-light">Artificial</span>
          <br />
          Intelligence &amp;
          <br />
          <span className="text-primary">Machine Learning</span>
        </h1>

        <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          A concept-first journey designed for university students in their final semester.
          Ten modules, clear explanations, hands-on diagrams and a certificate at the end.
        </p>

        {/* Name capture */}
        <form onSubmit={submit} className="mt-14 max-w-xl" data-testid="welcome-form">
          <label className="block text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
            Before we begin — your full name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Aryan Kapoor"
            className="name-input"
            data-testid="welcome-name-input"
            autoFocus
            maxLength={80}
          />

          <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
            <Shield size={13} className="mt-0.5 shrink-0 text-primary" />
            <p>
              This name will appear on your <strong>certificate of completion</strong> and
              <strong> cannot be changed later</strong>. Please double-check the spelling.
            </p>
          </div>

          {err && (
            <div className="mt-3 text-sm text-destructive" data-testid="welcome-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loadingBtn}
            data-testid="welcome-submit-btn"
            className="mt-8 inline-flex items-center gap-3 px-6 py-3 bg-primary text-primary-foreground text-sm font-medium rounded hover:brightness-110 transition-all disabled:opacity-60 group"
          >
            {loadingBtn ? "Saving…" : "Begin my training"}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </form>

        <div className="mt-20 border-t border-border pt-6 text-xs text-muted-foreground flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <span>© {new Date().getFullYear()} Steadfast IT Consulting</span>
          <span>
            Name correction? Write to{" "}
            <a
              href="mailto:operations@steadfastitconsulting.in"
              className="text-primary hover:underline"
            >
              operations@steadfastitconsulting.in
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
